import { useContext } from 'react';
import { useGridMode } from 'contexts/GridModeContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useFeatureFlag } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { findPlayerTournamentRecord, isOutOfTournament } from 'hooks/useLeaveTable';
import { useTableSelector } from 'hooks/useTableSelector';
import { removeTable } from 'reducers/tables';
import useGameService from 'services/GameService';
import { markPlayerExitedTable } from 'utils/recentlyExitedTables';

import { landOnSitGoTab } from 'components/SitGoResult/sitGoLobbyLanding';

/**
 * Leave a tournament table from the in-table tournament screens and move on:
 * the next open table, or the lobby when it was the last. Shared by the
 * tournament screen modal's Exit Table / Unregister and the Sit & Go waiting
 * panel's Unregister (EN-11257), so the two ways off a pre-start Sit & Go
 * cannot drift apart. Callers reset their own screen state first.
 *
 * For a Sit & Go (EN-11109): an exit from one the player is out of is kept, so
 * the engine dropping the table from GOT_PLAYER_GAMES and listing it again does
 * not bring it back; and with release.sit-go-result-modal on, the last table
 * lands on the lobby's Sit & Go tab (`sitGoCapacity` picks the played format).
 */
export const useTournamentTableExit = () => {
  const { leaveTable } = useGameService();
  const dispatch = useAppDispatch();
  const isGridMode = useGridMode();
  const { returnToLobby, openTable } = useContext(ViewManagerContext);
  // The panel's own table (TableIdContext). The selector's game can read the
  // last table to broadcast while this slice is unhydrated.
  const contextTableId = useContext(TableIdContext);
  const game = useTableSelector((table) => table.game);
  // All open tables (grid cells / single-view felts): whether to fall back to
  // the lobby or hop to another still-open table.
  const openTables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const lobbyPlayerTournamentDetails = useAppSelector(
    (state) => (state as any).lobby?.playerTournamentDetails
  );
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);
  const guid = useAppSelector((state) => state.user.playerGUID);
  const sitGoResultModal = useFeatureFlag('sitGoResultModal');

  return ({ sitGoCapacity = 0 }: { sitGoCapacity?: number } = {}) => {
    const isSitGo = game?.tabletype === 't';
    // Tear down the table the player just busted out of. leaveTable emits
    // SITOUT (no-op for an already-busted player), writes recentlyExitedTables
    // so App.tsx's GOT_TEXAS / GOT_PLAYER_GAMES auto-resub can't re-hydrate
    // the slot before our cleanup propagates, and emits UNSUBSCRIBE so the
    // engine stops fanning out broadcasts. Then dispatch(removeTable) drops
    // the slice so the Lobby Header watcher chip clears immediately rather
    // than waiting for the next GOT_PLAYER_GAMES push.
    //
    // The engine keeps a busted player's table in GOT_PLAYER_GAMES, which
    // outlives that short mark (and a refresh wipes it), so record the
    // player's own exit too — once the leave was sent, as the felt's Exit
    // Table does. A re-entry seat releases it (releaseExitsForAssignedSeats).
    // All of it names the panel's own table, the one leaveTable unsubscribes.
    // If the details still seat the player here (the closed-table prompt, or a
    // bust the details haven't caught up with), that seat is held, so its
    // repeated 'P' is not read as a re-entry that undoes the exit.
    const exitGameID = String(contextTableId || game?.gameID || '');
    if (exitGameID) {
      const heldSeat = (lobbyPlayerTournamentDetails || []).find(
        (d: any) => d?.status === 'P' && String(d?.gameID) === exitGameID
      );
      const heldSeatIn = heldSeat ? Number(heldSeat.tournamentId) : undefined;
      // A Sit & Go the player is out of: the engine dropping its table from
      // GOT_PLAYER_GAMES and listing it again must not undo the exit.
      const outOfTournament =
        isSitGo &&
        isOutOfTournament(
          findPlayerTournamentRecord(tournamentStatus, guid, game?.tournamentId),
          game?.status
        );
      if (leaveTable(true, true)) {
        markPlayerExitedTable(exitGameID, { heldSeatIn, outOfTournament });
      }
      dispatch(removeTable(exitGameID));
    }

    // In multi-table (grid) view we must NOT navigate — that would eject the
    // player from the whole grid when they only exited one table. Removing this
    // cell's slice above is enough; MultiTableGrid drops the player to the lobby
    // on its own if that was the last open table.
    if (isGridMode) return;

    // Single-table view: only fall back to the lobby if this was the player's
    // LAST open table. If they still have other tables open, hop to one of them
    // instead of dumping them in the lobby (reported bug: Exit always went to the
    // lobby even with other tables open). openTable routes to the grid when
    // multi-table is enabled, or opens the table in single view otherwise.
    const remaining = Object.entries(openTables || {}).filter(
      ([id, t]: [string, any]) =>
        id !== exitGameID && t?.game?.gameID && String(t.game.gameID) !== '0'
    );
    if (remaining.length > 0) {
      const [nextId, nextTable] = remaining[0];
      openTable(nextId, nextTable?.game?.game || 'texas');
    } else {
      // EN-11109: a Sit & Go lands on the lobby's Sit & Go tab (flag on only).
      if (sitGoResultModal && isSitGo) landOnSitGoTab(sitGoCapacity);
      returnToLobby();
    }
  };
};
